import {
  EmailCallContextPayloadI,
  GenerateEmailRequestParamsI,
} from "shared/lib/interfaces/email/ai";
import { CALL_CONTEXT_MIN_LENGTH } from "shared/lib/interfaces/email/call-context";

import { GetEmailGenerationInfoResponseI } from "@/api/routes/email";

export interface BuildGenerateEmailParamsArgsI {
  /** Everything `fetch_info_for_email_generation` returned for this template. */
  info: GetEmailGenerationInfoResponseI;

  /**
   * The prompt as it stands in the textarea, when the rep has one open.
   *
   * `info.user_prompt` is the template's prompt as last persisted, and the
   * textarea saves on a 3 second throttle with no flush. Composing inside that
   * window generated from the previous text - the rep writes a prompt, clicks
   * Compose, and watches the old one run. Taking the on-screen value removes
   * the race rather than narrowing it.
   */
  promptDraft?: string;

  /** The call context as it stands in the panel, saved or not. */
  callContext?: EmailCallContextPayloadI;

  /** Present only when this email is a step in a sequence. */
  sequenceOrder?: number;
}

/**
 * Number of characters in a string, counted the way the server counts them.
 *
 * `String.length` counts UTF-16 code units, so an emoji counts twice and a
 * transcript that the panel and the server both consider long enough can look
 * too short - or the reverse. Python's `len` and Go's validator both count
 * code points, which is what spreading into an array gives.
 */
const countCharacters = (text: string) => Array.from(text).length;

/**
 * Assemble the body of `POST /email/generate`.
 *
 * Pure on purpose: this is where every precedence rule in the compose flow
 * lives, and none of them are observable from the network tab once they are
 * wrong.
 */
export const buildGenerateEmailParams = ({
  info,
  promptDraft,
  callContext,
  sequenceOrder,
}: BuildGenerateEmailParamsArgsI): GenerateEmailRequestParamsI => {
  const trimmedDraft = promptDraft?.trim();

  const trimmedCallContext = callContext?.content?.trim() || "";
  const hasUsableCallContext =
    countCharacters(trimmedCallContext) >= CALL_CONTEXT_MIN_LENGTH;

  return {
    campaign_id: info.campaign_id,
    account_id: info.account_id,
    contact_id: info.contact_id,
    contact_email: info.contact_email,
    contact_name: info.contact_name,
    contact_title: info.contact_title,

    account_name: info.account_name,
    account_industry: info.account_industry,
    account_city: info.account_city,
    account_state: info.account_state,
    account_headcount: info.account_headcount,
    account_website: info.account_website,

    email_signature: info.email_signature,
    caller_name: info.caller_name,
    campaign_name: info.campaign_name,

    // An empty textarea is not an instruction to ignore the template, so the
    // saved prompt stands. Only text the rep actually typed overrides it.
    user_prompt: trimmedDraft || info.user_prompt,

    // Below the minimum the server would discard it anyway; omitting it here
    // keeps the request honest about what was sent.
    ...(hasUsableCallContext && callContext
      ? {
          call_context: {
            source: callContext.source,
            content: trimmedCallContext,
          },
        }
      : {}),

    ...(sequenceOrder ? { sequence_order: sequenceOrder } : {}),
  };
};
